import { gql, useApolloClient, useMutation } from '@apollo/client';
import { translateBackend } from '@assets/i18n/config';
import { LocalizedDatePicker } from '@components/fields/localizedDatePicker';
import AmountField from '@components/inputs/amountField';
import { BankAccountSelect } from '@components/inputs/bankAccountSelect';
import ExternalInstitutionSelect from '@components/inputs/externalInstitutionSelect';
import {
  SubAccountSelect,
  type SubAccountSelectData,
} from '@components/inputs/subAccountSelect';
import CreateNewModal from '@components/modals/createNewModal';
import { FeatureFlagTypes } from '@generated/graphql';
import { AccountTypes } from '@interfaces/account';
import { TransferType, WithdrawalMode } from '@interfaces/transfer';
import {
  FormControlLabel,
  FormGroup,
  ListItem,
  MenuItem,
  TextField,
} from '@mui/material';
import { ExternalLink } from '@ovComponents/1-primative/link/externalLink';
import { Radio, RadioGroup } from '@ovComponents/2-component/radio/radio';
import CalculateFeesForNewTransfer, {
  type WithdrawFee,
} from '@ovComponents/4-module/transfersTable/calculateFeesForNewTransfer';
import { FETCH_TRANSFERS } from '@ovComponents/4-module/transfersTable/oneoffTransfersQueries';
import { FETCH_SCHEDULED_TRANSFERS } from '@ovComponents/4-module/transfersTable/scheduledTransfersQueries';
import { isBlockedByRestrictedAccountWithdraw } from '@ovComponents/4-module/widgets/transfers/utils';
import { colors } from '@ovComponents/legacyColor';
import { TransferErrorBanner } from '@ovComponents/transferErrorBanner';
import {
  UserContext,
  useOrganizationFeatureFlag,
  usePermission,
} from '@providers/userContextProvider';
import { DEFAULT_SUPPORT_URL } from '@utils/constants';
import { fetchAuthorizedIndividual } from '@utils/fetchAuthorizedIndividual';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  Box,
  Checkbox,
  Stack,
  Typography,
  useSnackbar,
  useTheme,
} from 'hephaestus/web';
import { pick } from 'lodash-es';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

dayjs.extend(utc);

export const CREATE_ADJUSTMENT_TRANSFER = gql`
  mutation createAdjustmentTransfer($input: CreateAdjustmentTransferInput!) {
    createAdjustmentTransfer(input: $input) {
      transfer {
        id
      }
    }
  }
`;

export const CREATE_EFT_DEPOSIT_TRANSFER = gql`
  mutation createDepositTransfer($input: CreateDepositTransferInput!) {
    createDepositTransfer(input: $input) {
      transfer {
        id
      }
    }
  }
`;

export const CREATE_EFT_WITHDRAW_TRANSFER = gql`
  mutation createWithdrawTransfer($input: CreateWithdrawTransferInput!) {
    createWithdrawTransfer(input: $input) {
      transfer {
        id
      }
    }
  }
`;

export const CREATE_EFT_WITHDRAW_ALL_TRANSFER = gql`
  mutation withdrawAllFromSubAccount($input: WithdrawAllSubAccountInput!) {
    withdrawAllFromSubAccount(input: $input) {
      transfer {
        id
      }
    }
  }
`;

export const CREATE_TRANSFER_BETWEEN_SUB_ACCOUNT = gql`
  mutation transferBetweenSubAccounts(
    $input: TransferBetweenSubAccountsInput!
  ) {
    transferBetweenSubAccounts(input: $input) {
      transfer {
        id
      }
    }
  }
`;

export const CREATE_TRANSFER_ALL_BETWEEN_SUB_ACCOUNT = gql`
  mutation transferAllBetweenSubAccounts(
    $input: TransferAllBetweenSubAccountsInput!
  ) {
    transferAllBetweenSubAccounts(input: $input) {
      transfer {
        id
      }
    }
  }
`;

export const CREATE_EXTERNAL_TRANSFER = gql`
  mutation createExternalTransfer($input: CreateExternalTransferInput!) {
    createExternalTransfer(input: $input) {
      manualProcess {
        id
      }
    }
  }
`;

export const CREATE_SCHEDULED_DEPOSIT_TRANSFER = gql`
  mutation createScheduledDepositTransfer(
    $input: CreateScheduledDepositTransferInput!
  ) {
    createScheduledDepositTransfer(input: $input) {
      scheduledTransfer {
        id
        scheduledDate
      }
    }
  }
`;

export const CREATE_SCHEDULED_WITHDRAW_TRANSFER = gql`
  mutation createScheduledWithdrawTransfer(
    $input: CreateScheduledWithdrawTransferInput!
  ) {
    createScheduledWithdrawTransfer(input: $input) {
      scheduledTransfer {
        id
      }
    }
  }
`;

export const CREATE_CHEQUE_TRANSFER = gql`
  mutation createChequeTransfer($input: CreateChequeTransferInput!) {
    createChequeTransfer(input: $input) {
      transfer {
        id
      }
    }
  }
`;

export const CREATE_SCHEDULED_TRANSFER_BETWEEN_SUB_ACCOUNT = gql`
  mutation createScheduledTransferBetweenSubAccounts(
    $input: CreateScheduledTransferBetweenSubAccountsInput!
  ) {
    createScheduledTransferBetweenSubAccounts(input: $input) {
      scheduledTransfer {
        id
      }
    }
  }
`;

export type TransferProp = {
  type: string;
  source: string;
  frequency: string;
  scheduledDate?: string;
  subAccountId: string;
  subAccountMarketValueCents: number;
  subAccountUserId?: string;
  toSubAccountId: string;
  bankAccountId: string;
  amountCents?: number;
  clbAmountCents?: number;
  bctesgAmountCents?: number;
  adjustmentReason: string;
  isWithdrawAll: boolean;
  institution: {
    id: string;
  };
  transferAccountNumber: string;
  transferMethod: string;
  withdrawalMode: string;
  netAmountCents?: number;
  currency?: string;
};

const emptyFormState: TransferProp = {
  type: 'DEPOSIT',
  source: 'ADJUSTMENT',
  frequency: 'ONE_TIME',
  scheduledDate: undefined,
  subAccountId: '',
  subAccountMarketValueCents: 0,
  subAccountUserId: undefined,
  toSubAccountId: '',
  bankAccountId: '',
  amountCents: undefined,
  clbAmountCents: undefined,
  bctesgAmountCents: undefined,
  adjustmentReason: '',
  isWithdrawAll: false,
  institution: {
    id: '',
  },
  transferAccountNumber: '',
  transferMethod: 'ALL_IN_CASH',
  withdrawalMode: 'GROSS',
  netAmountCents: undefined,
};

const NewTransfer = ({
  afterCreate,
  forObject,
  forId,
  longLabel = false,
  presetSourceEFT = false,
  presetSubaccount,
  presetAccount,
  presetSubAccountMarketValueCents,
  disableButton,
}: {
  afterCreate: () => void;
  forObject: 'USER' | 'CLIENT_GROUP';
  forId: string;
  longLabel?: boolean;
  presetSourceEFT?: boolean;
  presetSubaccount?: string;
  presetAccount?: string;
  presetSubAccountMarketValueCents?: number;
  disableButton?: boolean;
}) => {
  const { t } = useTranslation(['components', 'shared']);
  const canAdjustRestrictedAccounts = usePermission(
    'write:registered_adjustment_transfer',
  );
  const canReadBankAccountDisclosed = usePermission(
    'read:bank_account_disclosed',
  );
  const canReadAlertReportBasic = usePermission('read:alert_report_basic');
  const canWriteAlertReportBasic = usePermission('write:alert_report_basic');
  const canReadClientLowRiskPii = usePermission('read:client_low_risk_pii');
  const canWriteTransferOboClients = usePermission(
    'write:transfer_obo_clients',
  );
  const canWriteTransferBasic = usePermission('write:transfer_basic');
  const transfersQueryPermissions = {
    canReadBankAccountDisclosed,
    canReadAlertReportBasic,
    canWriteAlertReportBasic,
    canReadClientLowRiskPii,
  };
  const scheduledTransfersQueryPermissions = {
    canReadClientLowRiskPii,
  };
  const { enqueueSnackbar } = useSnackbar();
  const [starting, setStarting] = useState<'now' | 'futureDate'>('now');
  const [transfer, setTransfer] = useState<TransferProp>(emptyFormState);
  const [initialState] = useState(emptyFormState);
  const graphqlClient = useApolloClient();
  const [selectedPortfolioAccountType, setSelectedPortfolioAccountType] =
    useState('');
  const [accountId, setAccountId] = useState<string>('');
  const [fee, setFee] = useState<number>(0);
  const { activeOrganization, userContext } = useContext(UserContext);
  const enableInKind = useOrganizationFeatureFlag(
    FeatureFlagTypes.InKindTransfers,
  );
  const minRecurringDeposit =
    activeOrganization.minRecurringDepositCents &&
    activeOrganization.minRecurringDepositCents;
  const theme = useTheme();
  const supportUrl =
    translateBackend(userContext.organization?.translatedSupportUrl) ||
    DEFAULT_SUPPORT_URL;
  const checkMinimumRecurringDeposit: boolean =
    ((starting === 'futureDate' ||
      starting !== 'now' ||
      transfer.frequency !== 'ONE_TIME') &&
      transfer.amountCents &&
      minRecurringDeposit &&
      transfer.amountCents < minRecurringDeposit) ||
    false;

  const displayWithdrawAll =
    (transfer.source === 'EFT' && transfer.type === 'WITHDRAW') ||
    transfer.source === 'TRANSFER_TO_SUB_ACCOUNT';
  const exceedWithdrawAllLimit: boolean =
    displayWithdrawAll &&
    transfer.subAccountMarketValueCents > 0 &&
    !!(
      (transfer.amountCents || transfer.netAmountCents) &&
      ((transfer.amountCents || transfer.netAmountCents) ?? 0) > 0 &&
      ((transfer.amountCents || transfer.netAmountCents) ?? 0) >
        0.98 * transfer.subAccountMarketValueCents
    );

  const isRESP = useMemo(() => {
    return [
      AccountTypes.RESP_SINGLE,
      AccountTypes.RESP_FAMILY,
      AccountTypes.RESP_ADULT,
      AccountTypes.RESP_SINGLE_JOINT,
      AccountTypes.RESP_FAMILY_JOINT,
    ].includes(selectedPortfolioAccountType as AccountTypes);
  }, [selectedPortfolioAccountType]);

  const isFHSA = useMemo(() => {
    return selectedPortfolioAccountType === AccountTypes.FHSA;
  }, [selectedPortfolioAccountType]);

  const isRespAndIsPartial = (): boolean =>
    transfer.source === 'EXTERNAL_TRANSFER_IN' &&
    isRESP &&
    transfer.transferMethod === 'PARTIAL_CASH';

  const onFeesReceived = (fees: WithdrawFee[]) => {
    if (
      transfer.type === TransferType.Withdraw &&
      transfer.withdrawalMode === WithdrawalMode.Net
    ) {
      const withholdingTax =
        fees.find((item: WithdrawFee) => item.type === 'WITHHOLDING_TAX')
          ?.amountCents ?? 0;

      setFee(withholdingTax);
    }
  };

  const getNetAmountWithFees = () => {
    if (
      transfer.type === TransferType.Withdraw &&
      transfer.withdrawalMode === WithdrawalMode.Net &&
      transfer.netAmountCents
    ) {
      return transfer.netAmountCents + (fee ?? 0);
    }

    return null;
  };

  const [createAdjustmentTransfer, opt1] = useMutation(
    CREATE_ADJUSTMENT_TRANSFER,
    {
      variables: {
        input: pick(transfer, [
          'adjustmentReason',
          'type',
          'subAccountId',
          'amountCents',
        ]),
      },
      refetchQueries: [FETCH_TRANSFERS(transfersQueryPermissions)],
    },
  );

  const [createEtfDepositTransfer, opt2] = useMutation(
    CREATE_EFT_DEPOSIT_TRANSFER,
    {
      variables: {
        input: pick(transfer, ['subAccountId', 'bankAccountId', 'amountCents']),
      },
      refetchQueries: [FETCH_TRANSFERS(transfersQueryPermissions)],
    },
  );

  const [createEtfWithdrawTransfer, opt3] = useMutation(
    CREATE_EFT_WITHDRAW_TRANSFER,
    {
      variables: {
        input: {
          ...pick(transfer, ['subAccountId', 'bankAccountId', 'amountCents']),
          ...(getNetAmountWithFees() && {
            amountCents: getNetAmountWithFees(),
          }),
          ...(transfer?.withdrawalMode && {
            withdrawalMode: transfer.withdrawalMode,
          }),
        },
      },
      refetchQueries: [FETCH_TRANSFERS(transfersQueryPermissions)],
    },
  );

  const [createEtfWithdrawAllTransfer, opt4] = useMutation(
    CREATE_EFT_WITHDRAW_ALL_TRANSFER,
    {
      variables: {
        input: pick(transfer, ['subAccountId', 'bankAccountId']),
      },
      refetchQueries: [FETCH_TRANSFERS(transfersQueryPermissions)],
    },
  );

  const [createTransferBetweenSubAccounts, opt5] = useMutation(
    CREATE_TRANSFER_BETWEEN_SUB_ACCOUNT,
    {
      variables: {
        input: {
          ...pick(transfer, ['toSubAccountId', 'amountCents', 'currency']),
          fromSubAccountId: transfer.subAccountId,
        },
      },
      refetchQueries: [FETCH_TRANSFERS(transfersQueryPermissions)],
    },
  );

  const [createTransferAllBetweenSubAccounts, opt6] = useMutation(
    CREATE_TRANSFER_ALL_BETWEEN_SUB_ACCOUNT,
    {
      variables: {
        input: {
          ...pick(transfer, ['toSubAccountId', 'currency']),
          fromSubAccountId: transfer.subAccountId,
        },
      },
      refetchQueries: [FETCH_TRANSFERS(transfersQueryPermissions)],
    },
  );

  const [createExternalTransfer, opt7] = useMutation(CREATE_EXTERNAL_TRANSFER);

  const [createEtfScheduledDepositTransfer, opt8] = useMutation(
    CREATE_SCHEDULED_DEPOSIT_TRANSFER,
    {
      variables: {
        input: pick(transfer, [
          'amountCents',
          'bankAccountId',
          'frequency',
          'scheduledDate',
          'subAccountId',
        ]),
      },
      refetchQueries: [
        FETCH_SCHEDULED_TRANSFERS(scheduledTransfersQueryPermissions),
      ],
    },
  );

  const [createEtfScheduledWithdrawTransfer, opt9] = useMutation(
    CREATE_SCHEDULED_WITHDRAW_TRANSFER,
    {
      variables: {
        input: {
          ...pick(transfer, [
            'amountCents',
            'bankAccountId',
            'frequency',
            'scheduledDate',
            'subAccountId',
          ]),
          ...(getNetAmountWithFees() && {
            amountCents: getNetAmountWithFees(),
          }),
        },
      },
      refetchQueries: [
        FETCH_SCHEDULED_TRANSFERS(scheduledTransfersQueryPermissions),
      ],
    },
  );

  const [createChequeTransfer, opt10] = useMutation(CREATE_CHEQUE_TRANSFER, {
    variables: {
      input: pick(transfer, ['subAccountId', 'amountCents', 'type']),
    },
    refetchQueries: [FETCH_TRANSFERS(transfersQueryPermissions)],
  });

  const [createScheduledTransferBetweenSubAccounts, opt11] = useMutation(
    CREATE_SCHEDULED_TRANSFER_BETWEEN_SUB_ACCOUNT,
    {
      variables: {
        input: {
          ...pick(transfer, [
            'toSubAccountId',
            'amountCents',
            'frequency',
            'scheduledDate',
            'currency',
          ]),
          fromSubAccountId: transfer.subAccountId,
          transferAll: transfer.isWithdrawAll,
        },
      },
      refetchQueries: [
        FETCH_SCHEDULED_TRANSFERS(scheduledTransfersQueryPermissions),
      ],
    },
  );

  const presetTransfer = () => {
    if (presetAccount) setAccountId(presetAccount);

    setTransfer({
      ...emptyFormState,
      ...(presetSourceEFT && { source: 'EFT' }),
      ...(presetSubaccount && { subAccountId: presetSubaccount }),
      ...(presetSubAccountMarketValueCents && {
        subAccountMarketValueCents: presetSubAccountMarketValueCents,
      }),
    });

    setStarting('now');
  };
  const [isSubmitting, setIsSubmitting] = useState(false);

  const create = async () => {
    let response;
    if (isSubmitting) return; // Prevent further submission if it's already submitting
    setIsSubmitting(true); // Disable button

    if (transfer.source === 'ADJUSTMENT') {
      response = await createAdjustmentTransfer();
    } else if (transfer.source === 'EFT') {
      if (transfer.type === 'DEPOSIT' && starting === 'futureDate') {
        response = await createEtfScheduledDepositTransfer();
      } else if (transfer.type === 'DEPOSIT') {
        response = await createEtfDepositTransfer();
      } else if (transfer.type === 'WITHDRAW' && starting === 'futureDate') {
        response = await createEtfScheduledWithdrawTransfer();
      } else if (!transfer.isWithdrawAll) {
        response = await createEtfWithdrawTransfer();
      } else {
        response = await createEtfWithdrawAllTransfer();
      }
    } else if (transfer.source === 'TRANSFER_TO_SUB_ACCOUNT') {
      if (starting === 'futureDate') {
        response = await createScheduledTransferBetweenSubAccounts();
      } else if (!transfer.isWithdrawAll) {
        response = await createTransferBetweenSubAccounts();
      } else {
        response = await createTransferAllBetweenSubAccounts();
      }
    } else if (transfer.source === 'EXTERNAL_TRANSFER_IN') {
      let responsibleUserId = null;
      if (forObject === 'USER' && userContext.id) {
        responsibleUserId = await fetchAuthorizedIndividual({
          userId: forId,
          apolloClient: graphqlClient,
          userContext,
        });
      }
      const transferInput = isRespAndIsPartial()
        ? pick(transfer, [
            'subAccountId',
            'transferMethod',
            'amountCents',
            'institution',
            'transferAccountNumber',
            'clbAmountCents',
            'bctesgAmountCents',
          ])
        : pick(transfer, [
            'subAccountId',
            'transferMethod',
            'amountCents',
            'institution',
            'transferAccountNumber',
          ]);
      response = await createExternalTransfer({
        variables: {
          input: {
            ...transferInput,
            createdByUserId:
              responsibleUserId && responsibleUserId !== forId
                ? responsibleUserId
                : undefined,
          },
        },
        refetchQueries: [FETCH_TRANSFERS(transfersQueryPermissions)],
      });
    } else if (transfer.source === 'CHEQUE') {
      response = await createChequeTransfer();
    }

    if (response?.data) {
      enqueueSnackbar(t('components:transferModal.createdToastMessage'), {
        variant: 'info',
      });
    }
    setIsSubmitting(false);
    afterCreate();
    presetTransfer();
  };

  const close = () => {
    presetTransfer();
  };

  useEffect(() => {
    presetTransfer();
  }, [
    presetSourceEFT,
    presetSubaccount,
    presetAccount,
    presetSubAccountMarketValueCents,
  ]);

  const loading =
    opt1.loading ||
    opt2.loading ||
    opt3.loading ||
    opt4.loading ||
    opt5.loading ||
    opt6.loading ||
    opt7.loading ||
    opt8.loading ||
    opt9.loading ||
    opt10.loading ||
    opt11.loading;
  const disabled =
    !(
      transfer.subAccountId &&
      !exceedWithdrawAllLimit &&
      (transfer.amountCents ||
        transfer.netAmountCents ||
        transfer.isWithdrawAll) &&
      ((transfer.source === 'ADJUSTMENT' && transfer.adjustmentReason) ||
        (transfer.source === 'EFT' && transfer.bankAccountId) ||
        (transfer.source === 'EXTERNAL_TRANSFER_IN' &&
          transfer.transferMethod &&
          transfer.institution &&
          transfer.transferAccountNumber) ||
        (transfer.source === 'TRANSFER_TO_SUB_ACCOUNT' &&
          transfer.toSubAccountId) ||
        transfer.source === 'CHEQUE')
    ) || checkMinimumRecurringDeposit;

  const scheduledDateInputRef = useRef<HTMLInputElement>(null);

  const isRespWithdraw = useMemo(() => {
    if (!transfer.type) return false;
    return transfer.type === 'WITHDRAW' && isRESP;
  }, [transfer, isRESP]);

  const isFHSAWithdraw = useMemo(() => {
    if (!transfer.type) return false;
    return transfer.type === 'WITHDRAW' && isFHSA;
  }, [transfer, isFHSA]);

  const isBlockedByAccountType = useMemo(
    () =>
      isBlockedByRestrictedAccountWithdraw({
        isRespWithdraw,
        isFHSAWithdraw,
        transferSource: transfer.source,
        canAdjustRestrictedAccounts,
      }),
    [
      isRespWithdraw,
      isFHSAWithdraw,
      transfer.source,
      canAdjustRestrictedAccounts,
    ],
  );

  /* pre-open date picker for frequencies = weekly, monthly, etc. */
  useEffect(() => {
    if (transfer.frequency !== 'ONE_TIME') {
      setStarting('futureDate');
      scheduledDateInputRef.current?.click();
    }
  }, [transfer.frequency]);

  /* pre-open date picker for "just this once" with future date */
  useEffect(() => {
    if (starting === 'futureDate') {
      setTransfer((previous) => ({
        ...previous,
        scheduledDate: dayjs().add(1, 'day').format('YYYY-MM-DD'),
      }));
      scheduledDateInputRef.current?.click();
    }
    if (starting === 'now') {
      setTransfer((previous) => ({ ...previous, scheduledDate: undefined }));
    }
  }, [starting]);

  const isInitiatedByRepresentative = () =>
    canWriteTransferOboClients && !canWriteTransferBasic;

  return (
    <CreateNewModal
      loading={loading}
      state={transfer}
      initialState={initialState}
      disableButton={disableButton}
      disabled={isBlockedByAccountType || disabled}
      title={t('components:transferModal.title')}
      onSubmit={create}
      {...(longLabel && { modalButton: t('transfersTable.addTransfer') })}
      onClose={close}
    >
      <ListItem>
        <TextField
          select
          value={transfer.source}
          label={t('components:transferModal.source')}
          fullWidth
          onChange={(e: any) => {
            setTransfer({ ...transfer, source: e.target.value });
          }}
        >
          <MenuItem value='ADJUSTMENT'>
            {t('components:transferModal.sources.adjustment')}
          </MenuItem>
          <MenuItem value='EFT'>
            {t('components:transferModal.sources.eft')}
          </MenuItem>
          <MenuItem
            value='EXTERNAL_TRANSFER_IN'
            data-testid='EXTERNAL_TRANSFER_IN'
          >
            {t('components:transferModal.sources.externalTransferIn')}
          </MenuItem>
          <MenuItem value='TRANSFER_TO_SUB_ACCOUNT'>
            {t('components:transferModal.sources.transferToSubAccount')}
          </MenuItem>
          <MenuItem value='CHEQUE'>
            {t('components:transferModal.sources.cheque')}
          </MenuItem>
        </TextField>
      </ListItem>
      {transfer.source === 'EXTERNAL_TRANSFER_IN' && (
        <ListItem>
          <ExternalInstitutionSelect
            setInstitution={(e) =>
              setTransfer({ ...transfer, institution: { id: e?.id ?? '' } })
            }
            value={transfer.institution.id}
            label={t('components:transferModal.externalTransfer.institution')}
          />
        </ListItem>
      )}
      {transfer.source === 'EXTERNAL_TRANSFER_IN' && (
        <ListItem>
          <TextField
            select
            value={transfer.transferMethod}
            label={t('components:transferModal.externalTransfer.method')}
            fullWidth
            onChange={(e: any) => {
              setTransfer({ ...transfer, transferMethod: e.target.value });
            }}
          >
            <MenuItem value='ALL_IN_CASH'>
              {t('components:transferModal.externalTransfer.allInCash')}
            </MenuItem>
            <MenuItem value='PARTIAL_CASH'>
              {t('components:transferModal.externalTransfer.partialCash')}
            </MenuItem>
            {enableInKind && (
              <MenuItem value='ALL_IN_KIND'>
                {t('components:transferModal.externalTransfer.allInKind')}
              </MenuItem>
            )}
          </TextField>
        </ListItem>
      )}
      {['ADJUSTMENT', 'EFT', 'CHEQUE'].includes(transfer.source) && (
        <ListItem>
          <TextField
            select
            value={transfer.type}
            label={t('components:transferModal.type')}
            fullWidth
            onChange={(e: any) =>
              setTransfer({ ...transfer, type: e.target.value })
            }
          >
            <MenuItem value='DEPOSIT'>
              {t('components:transferModal.deposit')}
            </MenuItem>
            <MenuItem value='WITHDRAW'>
              {t('components:transferModal.withdraw')}
            </MenuItem>
          </TextField>
        </ListItem>
      )}
      {transfer.type &&
        transfer.type === TransferType.Withdraw &&
        transfer.source === 'EFT' && (
          <ListItem>
            <TextField
              select
              value={transfer.withdrawalMode}
              label={t('components:transferModal.withdrawalMode')}
              fullWidth
              onChange={(e: any) => {
                setTransfer({
                  ...transfer,
                  withdrawalMode: e.target.value,
                  amountCents: 0,
                });
              }}
            >
              <MenuItem value={WithdrawalMode.Gross}>
                {t('components:transferModal.gross')}
              </MenuItem>
              <MenuItem value={WithdrawalMode.Net}>
                {t('components:transferModal.net')}
              </MenuItem>
            </TextField>
          </ListItem>
        )}
      <ListItem>
        <SubAccountSelect
          setSubAccount={(e) =>
            setTransfer((prev) => ({
              ...prev,
              subAccountId: e.target.value,
              toSubAccountId:
                prev.toSubAccountId === e.target.value
                  ? ''
                  : prev.toSubAccountId,
            }))
          }
          setSubAccountData={(data: SubAccountSelectData) => {
            setTransfer((prev) => ({
              ...prev,
              subAccountMarketValueCents: data.statistics.marketValueCents,
              subAccountUserId: data.account.user?.id,
              bankAccountId:
                transfer.subAccountUserId !== data.account.user?.id
                  ? ''
                  : transfer.bankAccountId,
            }));
            setSelectedPortfolioAccountType(data.account.type);
            setAccountId(data.account.id);
          }}
          value={transfer.subAccountId}
          label={
            transfer.source === 'TRANSFER_TO_SUB_ACCOUNT'
              ? t('components:transferModal.fromPortfolio')
              : t('components:transferModal.portfolio')
          }
          forObject={forObject}
          forId={forId}
        />
      </ListItem>
      {transfer.source === 'TRANSFER_TO_SUB_ACCOUNT' && (
        <ListItem>
          <SubAccountSelect
            setSubAccount={(e) =>
              setTransfer({ ...transfer, toSubAccountId: e.target.value })
            }
            value={transfer.toSubAccountId}
            label={t('components:transferModal.toPortfolio')}
            forObject={forObject}
            forId={forId}
            excludedIds={[transfer.subAccountId]}
          />
        </ListItem>
      )}
      {transfer.source === 'EFT' && (
        <ListItem>
          <BankAccountSelect
            setBankAccount={(value: string) =>
              setTransfer({ ...transfer, bankAccountId: value })
            }
            value={transfer.bankAccountId}
            label={t('components:transferModal.bankAccount')}
            disabled={
              forObject === 'CLIENT_GROUP' &&
              transfer.subAccountUserId === undefined
            }
            userId={
              forObject === 'USER' ? forId : (transfer.subAccountUserId ?? '')
            }
          />
        </ListItem>
      )}
      {transfer.source === 'EXTERNAL_TRANSFER_IN' && (
        <ListItem>
          <TextField
            label={t(
              'components:transferModal.externalTransfer.transferAccountNumber',
            )}
            variant='outlined'
            data-testid='transfer-acc-input'
            value={transfer.transferAccountNumber}
            fullWidth
            onChange={(e) =>
              setTransfer({
                ...transfer,
                transferAccountNumber: e.target.value,
              })
            }
          />
        </ListItem>
      )}
      {transfer.type === TransferType.Withdraw &&
        transfer.withdrawalMode === WithdrawalMode.Net &&
        !transfer.isWithdrawAll && (
          <ListItem>
            <AmountField
              label={t('components:transferModal.netAmount')}
              variant='outlined'
              value={transfer.netAmountCents}
              fullWidth
              onChange={(e: any) => {
                const { valueCents } = e.target;

                setTransfer({ ...transfer, netAmountCents: valueCents });
              }}
              error={checkMinimumRecurringDeposit}
              helperText={
                checkMinimumRecurringDeposit &&
                minRecurringDeposit &&
                `${t('components:transferModal.minimumRecurringDepositAmount')} ${minRecurringDeposit / 100} $`
              }
            />
          </ListItem>
        )}

      {(transfer.type !== TransferType.Withdraw ||
        transfer.withdrawalMode !== WithdrawalMode.Net) && (
        <ListItem>
          <AmountField
            label={t(
              `components:transferModal.${isRespAndIsPartial() ? 'totalTransferAmount' : 'amount'}`,
            )}
            variant='outlined'
            value={transfer.amountCents}
            fullWidth
            onChange={(e: any) => {
              setTransfer({ ...transfer, amountCents: e.target.valueCents });
            }}
            error={checkMinimumRecurringDeposit}
            helperText={
              checkMinimumRecurringDeposit &&
              minRecurringDeposit &&
              `${t('components:transferModal.minimumRecurringDepositAmount')} $${minRecurringDeposit / 100}`
            }
            disabled={displayWithdrawAll && transfer.isWithdrawAll}
          />
        </ListItem>
      )}

      {['EFT'].includes(transfer.source) &&
        transfer.type === TransferType.Withdraw && (
          <CalculateFeesForNewTransfer
            transfer={transfer}
            accountId={accountId}
            exceedWithdrawAllLimit={exceedWithdrawAllLimit}
            onFeesReceived={(fees: WithdrawFee[]) => onFeesReceived(fees)}
          />
        )}

      {isRespAndIsPartial() && (
        <ListItem key='clbTransferAmount'>
          <AmountField
            label={t('components:transferModal.clbTransferAmount')}
            variant='outlined'
            value={transfer.clbAmountCents}
            fullWidth
            onChange={(e: any) => {
              setTransfer({ ...transfer, clbAmountCents: e.target.valueCents });
            }}
          />
        </ListItem>
      )}
      {isRespAndIsPartial() && (
        <ListItem key='bcGrantTransferAmount'>
          <AmountField
            label={t('components:transferModal.bcGrantTransferAmount')}
            variant='outlined'
            value={transfer.bctesgAmountCents}
            fullWidth
            onChange={(e: any) => {
              setTransfer({
                ...transfer,
                bctesgAmountCents: e.target.valueCents,
              });
            }}
          />
        </ListItem>
      )}
      {exceedWithdrawAllLimit && (
        <ListItem>
          <Typography
            variant='bodyMedium'
            sx={{ color: colors.warning, lineHeight: 1, fontWeight: 500 }}
          >
            {transfer.source === 'TRANSFER_TO_SUB_ACCOUNT'
              ? t('components:transferModal.exceedTransferAllWarning')
              : t('components:transferModal.exceedWithdrawAllWarning')}
          </Typography>
        </ListItem>
      )}
      {displayWithdrawAll && (
        <ListItem>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  color='primary'
                  checked={transfer.isWithdrawAll}
                  onChange={(e) =>
                    setTransfer({
                      ...transfer,
                      isWithdrawAll: e.target.checked,
                      amountCents: 0,
                      netAmountCents: 0,
                    })
                  }
                />
              }
              label={
                transfer.source === 'TRANSFER_TO_SUB_ACCOUNT'
                  ? t('components:transferModal.transferAll')
                  : t('components:transferModal.withdrawAll')
              }
              sx={{ ml: 0, gap: 1 }}
            />
          </FormGroup>
        </ListItem>
      )}
      {transfer.source === 'ADJUSTMENT' && (
        <ListItem>
          <TextField
            fullWidth
            label={t('components:transferModal.adjustmentReason')}
            variant='outlined'
            value={transfer.adjustmentReason}
            onChange={(e) =>
              setTransfer({ ...transfer, adjustmentReason: e.target.value })
            }
          />
        </ListItem>
      )}
      {(transfer.source === 'EFT' ||
        transfer.source === 'TRANSFER_TO_SUB_ACCOUNT') && (
        <>
          <ListItem>
            <TextField
              select
              value={transfer.frequency ?? ''}
              label={t('components:transferModal.schedule')}
              fullWidth
              data-testid='frequency-dropdown'
              onChange={(e: any) => {
                setTransfer({ ...transfer, frequency: e.target.value });
              }}
            >
              <MenuItem value='ONE_TIME'>
                {t('components:transferModal.scheduleOptions.ONE_TIME')}
              </MenuItem>
              <MenuItem value='WEEKLY'>
                {t('components:transferModal.scheduleOptions.WEEKLY')}
              </MenuItem>
              <MenuItem value='BI_WEEKLY'>
                {t('components:transferModal.scheduleOptions.BI_WEEKLY')}
              </MenuItem>
              <MenuItem value='MONTHLY'>
                {t('components:transferModal.scheduleOptions.MONTHLY')}
              </MenuItem>
              <MenuItem value='QUARTERLY'>
                {t('components:transferModal.scheduleOptions.QUARTERLY')}
              </MenuItem>
            </TextField>
          </ListItem>
          {transfer.frequency === 'ONE_TIME' && (
            <ListItem>
              <RadioGroup
                label={t('components:transferModal.starting')}
                value={starting}
                onChange={(e: any) => {
                  setStarting(e.target.value);
                }}
              >
                <Stack direction={'row'}>
                  {!isInitiatedByRepresentative() && (
                    <Radio
                      value='now'
                      label={t('components:transferModal.startingOptions.now')}
                    />
                  )}
                  <Radio
                    value='futureDate'
                    label={t(
                      'components:transferModal.startingOptions.futureDate',
                    )}
                  />
                </Stack>
              </RadioGroup>
            </ListItem>
          )}
          {(starting === 'futureDate' || transfer.frequency !== 'ONE_TIME') && (
            <ListItem>
              <LocalizedDatePicker
                label={
                  transfer.frequency === 'ONE_TIME'
                    ? t('components:transferModal.startingOptions.futureDate')
                    : t('components:transferModal.starting')
                }
                minDate={dayjs().add(1, 'day')}
                value={transfer.scheduledDate}
                onChange={(date) => {
                  setTransfer({
                    ...transfer,
                    scheduledDate: date
                      ? dayjs(date.toString()).format('YYYY-MM-DD')
                      : undefined,
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    fullWidth
                    {...params}
                    error={!transfer.scheduledDate}
                    autoFocus
                  />
                )}
                InputProps={{ inputRef: scheduledDateInputRef }}
              />
            </ListItem>
          )}
        </>
      )}
      {isRespWithdraw && isBlockedByAccountType && (
        <Box sx={{ m: 2 }}>
          <TransferErrorBanner
            theme={theme}
            i18nKey='transfer:withdrawal.withdrawRespAccountError'
            components={[
              <ExternalLink
                key='0'
                href='https://www.cifinancial.com/content/dam/ci-investment-services/forms/202309/CI IS- Registered Accounts RESP Withdrawal Form -E_202308.pdf'
                style={{ color: theme.color.negativeOutline }}
              />,
              <ExternalLink
                key='1'
                href={supportUrl}
                style={{ color: theme.color.negativeOutline }}
              />,
            ]}
          />
        </Box>
      )}
      {isFHSAWithdraw && isBlockedByAccountType && (
        <Box sx={{ m: 2 }}>
          <TransferErrorBanner
            theme={theme}
            i18nKey='transfer:withdrawal.withdrawFhsaAccountError'
            supportUrl={supportUrl}
            linkSx={{ color: theme.color.negativeOutline }}
          />
        </Box>
      )}
    </CreateNewModal>
  );
};

export default NewTransfer;
